import {
  HERO_SCENE_INTRO_ID,
  HERO_SCENE_NARROW_MEDIA,
  HERO_SWIRL_CLIP_ACTIVE_ATTR,
  HERO_SWIRL_CLIP_ID,
  HERO_SWIRL_CLIP_NAV_ID,
  HERO_SWIRL_CLIP_TARGET_SELECTOR,
  HERO_SWIRL_PROXY_WILL_CHANGE_IDLE,
  HERO_SWIRL_SCENE_DONE_ATTR,
} from '@/constants/heroScene';
import { loadHeroSwirlPerfMarks } from '@/lib/perfLab';
import { markHeroSceneReady, resetHeroSceneReady } from '@/lib/scrollRestoration';
import { syncScrollTriggerWithLenis } from '@/motion/scrollScene';
import { heroScene } from '@/motion/tokens';

import { HERO_SWIRL_CIRCLE_VIEWBOX } from './HeroSwirlCircle';
import { createIntroContentReveal } from './introContentReveal';

import type { ScrollSceneRuntime } from '@/motion/scrollScene';

const heroSwirlPerf =
  process.env.NEXT_PUBLIC_ENABLE_PERF_LAB === 'true'
    ? loadHeroSwirlPerfMarks()
    : null;

type HeroScrollSceneElements = {
  /** Scroll runway — trigger for the circle scrub (`HeroScrollScene` root). */
  runway: HTMLElement;
  /** Invisible transform proxy — geometry source of truth for the clip. */
  circle: HTMLElement;
  /** `<path>` inside the intro swirl `<clipPath>`; receives per-frame `transform`. */
  clipPathShape: SVGPathElement;
  /** `<path>` inside the StickyNav swirl `<clipPath>`; local-space transform. */
  navClipPathShape: SVGPathElement;
};

const INTRO_CLIP_VALUE = `url(#${HERO_SWIRL_CLIP_ID})`;
const NAV_CLIP_VALUE = `url(#${HERO_SWIRL_CLIP_NAV_ID})`;

const halfW = HERO_SWIRL_CIRCLE_VIEWBOX.width / 2;
const halfH = HERO_SWIRL_CIRCLE_VIEWBOX.height / 2;

function setClipStyles(element: HTMLElement, value: string) {
  element.style.setProperty('clip-path', value);
  element.style.setProperty('-webkit-clip-path', value);
}

function clearClipStyles(element: HTMLElement) {
  element.style.removeProperty('clip-path');
  element.style.removeProperty('-webkit-clip-path');
}

function writeClipTransform(
  path: SVGPathElement,
  localCenterX: number,
  localCenterY: number,
  rotation: number,
  k: number,
) {
  path.setAttribute(
    'transform',
    `translate(${localCenterX}, ${localCenterY}) rotate(${rotation}) scale(${k}) translate(${-halfW}, ${-halfH})`,
  );
}

/**
 * Hero → Introduction scroll choreography (see docs/04-motion.md):
 *
 * Motion OK (all widths):
 * 1. Sticky hero stays static while an invisible swirl-circle proxy scrubs up,
 *    spinning as it travels (transform-only, linked to the runway).
 * 2. `#introduction` is pulled up over the hero from scroll 0 (CSS) and pinned
 *    off the runway (`trigger: runway`, `start: 'top top'`) so it is fixed from
 *    the first scroll pixel — revealed by the moving swirl clip. StickyNav’s
 *    net in-flow height is cancelled so intro measures at viewport top (pin and
 *    unpin `translateY` stay continuous; no top-clamp hacks).
 * 3. While progress ∈ (0, 1), proxy geometry is written onto the SVG clipPath
 *    paths each scrub tick (Introduction / StickyNav fade through the mask).
 * 4. At progress ≥ 1 clips tear down (`releaseSwirlClips`, clear path
 *    transforms, drop proxy `will-change`) and `HERO_SWIRL_SCENE_DONE_ATTR`
 *    is set so the rest of the page paints unclipped.
 * 5. Scrubbing back (progress < 1) re-poses paths then re-applies clips so
 *    intro can fade out through the mask again.
 * 6. Intro content scrubs on the runway (`introHeaderReveal`).
 *
 * Narrow (`HERO_SCENE_NARROW_MEDIA`): ease-in `y`, linear scale/rotation so
 * size grows evenly under touch. `md+`: single tween with `power1.in`.
 *
 * Always uses boolean `scrub: true` — never a numeric scrub lag (iOS pauses
 * rAF during touch scroll, so numeric scrub freezes mid-gesture).
 *
 * Clip paths must be posed (`syncClipTransforms`) before `applySwirlClips`
 * unlocks `#introduction` visibility — otherwise the identity ~full swirl
 * flashes then pops to `circleStartScale`.
 *
 * Runs inside `gsap.matchMedia` gated by `prefers-reduced-motion: no-preference`;
 * reverting (or never matching) leaves the static, fully visible, unclipped layout.
 */
export function createHeroScrollScene(
  runtime: ScrollSceneRuntime,
  { runway, circle, clipPathShape, navClipPathShape }: HeroScrollSceneElements,
): () => void {
  const { gsap, ScrollTrigger } = runtime;
  const unsyncLenis = syncScrollTriggerWithLenis(ScrollTrigger);
  const matchMedia = gsap.matchMedia();

  // The runway/sticky-slot layout is pure CSS (active from first paint), so
  // triggers created after a late gsap load catch up to the current scroll
  // with no reflow. This matchMedia only gates the animation itself.
  matchMedia.add(
    {
      isMotionOk: '(prefers-reduced-motion: no-preference)',
      isNarrow: HERO_SCENE_NARROW_MEDIA,
    },
    (context) => {
      if (!context.conditions?.isMotionOk) return;

      // Boolean scrub only — numeric scrub (e.g. `1`) lerps on the ticker and
      // freezes mid-scroll on iOS where rAF pauses during touch/momentum.
      const scrub = true as const;
      const isNarrow = Boolean(context.conditions.isNarrow);
      const circleTravelEase = isNarrow
        ? heroScene.narrow.circleTravelEase
        : heroScene.circleTravelEase;

      const intro = document.getElementById(HERO_SCENE_INTRO_ID);
      const stickyNav = document.querySelector<HTMLElement>(
        HERO_SWIRL_CLIP_TARGET_SELECTOR,
      );

      // StickyNav between runway and intro leaves a net in-flow gap (height +
      // margin-block-end). That pushed #introduction below viewport top so the
      // runway pin baked a positive `top`, and unpin `translateY(pinChange)`
      // jumped by the same gap. Cancel the footprint so pin/unpin agree; keep
      // margin-block-start hero overlap from CSS.
      const syncStickyNavFlowGap = () => {
        if (!stickyNav) return;
        const height = stickyNav.offsetHeight;
        stickyNav.style.marginBottom = height > 0 ? `${-height}px` : '';
      };
      const clearStickyNavFlowGap = () => {
        stickyNav?.style.removeProperty('margin-bottom');
      };

      const applySwirlClips = () => {
        let styleWrites = 0;
        if (intro) {
          setClipStyles(intro, INTRO_CLIP_VALUE);
          styleWrites += 2;
          intro.setAttribute(HERO_SWIRL_CLIP_ACTIVE_ATTR, '');
          intro.removeAttribute(HERO_SWIRL_SCENE_DONE_ATTR);
        }
        if (stickyNav) {
          setClipStyles(stickyNav, NAV_CLIP_VALUE);
          styleWrites += 2;
        }
        if (heroSwirlPerf && styleWrites > 0) {
          void heroSwirlPerf.then((m) =>
            m.recordHeroSwirlClipStyleWrites(styleWrites),
          );
        }
      };

      const releaseSwirlClips = () => {
        let styleWrites = 0;
        if (intro) {
          setClipStyles(intro, 'none');
          styleWrites += 2;
          intro.removeAttribute(HERO_SWIRL_CLIP_ACTIVE_ATTR);
          intro.setAttribute(HERO_SWIRL_SCENE_DONE_ATTR, '');
        }
        if (stickyNav) {
          setClipStyles(stickyNav, 'none');
          styleWrites += 2;
        }
        if (heroSwirlPerf && styleWrites > 0) {
          void heroSwirlPerf.then((m) =>
            m.recordHeroSwirlClipStyleWrites(styleWrites),
          );
        }
      };

      const clearSwirlClipStyles = () => {
        if (intro) {
          clearClipStyles(intro);
          intro.removeAttribute(HERO_SWIRL_CLIP_ACTIVE_ATTR);
          intro.removeAttribute(HERO_SWIRL_SCENE_DONE_ATTR);
        }
        if (stickyNav) {
          clearClipStyles(stickyNav);
        }
      };

      // Cache circle proxy layout once per refresh; per scrub tick only read
      // GSAP props for the proxy. While the hero slot is sticky through the
      // runway, the circle's untransformed centre is stable in the viewport —
      // derive motion from `y`. Intro + StickyNav border-box origins are read
      // live each sync: sticky vs pin can diverge from refresh-time cache and
      // misalign the two `userSpaceOnUse` clipPaths in viewport space.
      const layoutCache = {
        ready: false,
        circleWidth: 0,
        circleHeight: 0,
        circleBaseCenterX: 0,
        circleBaseCenterY: 0,
        /** `WxH@innerWxinnerH` — skip ST refresh work when box + viewport unchanged. */
        sizeFingerprint: '',
      };

      const approxEq = (a: number, b: number) => Math.abs(a - b) < 0.5;

      /**
       * Pure-read circle layout snapshot — no `gsap.set` / style writes.
       * Rotated/scaled AABB around transform-origin center still shares that
       * centre; subtract GSAP `x`/`y` to recover the untransformed base used
       * by clip sync. Avoids the former write→getBoundingClientRect→restore
       * forced-reflow thrash on every ScrollTrigger refresh.
       */
      const refreshLayoutCache = () => {
        const x = Number(gsap.getProperty(circle, 'x')) || 0;
        const y = Number(gsap.getProperty(circle, 'y')) || 0;

        // Batch geometry reads only (no preceding style invalidation).
        const circleWidth = circle.offsetWidth;
        const circleHeight = circle.offsetHeight;
        const circleRect = circle.getBoundingClientRect();

        const circleBaseCenterX = circleRect.left + circleRect.width / 2 - x;
        const circleBaseCenterY = circleRect.top + circleRect.height / 2 - y;
        const sizeFingerprint = `${circleWidth}x${circleHeight}@${window.innerWidth}x${window.innerHeight}`;

        if (
          layoutCache.ready &&
          layoutCache.sizeFingerprint === sizeFingerprint &&
          approxEq(layoutCache.circleBaseCenterX, circleBaseCenterX) &&
          approxEq(layoutCache.circleBaseCenterY, circleBaseCenterY)
        ) {
          return;
        }

        layoutCache.circleWidth = circleWidth;
        layoutCache.circleHeight = circleHeight;
        layoutCache.circleBaseCenterX = circleBaseCenterX;
        layoutCache.circleBaseCenterY = circleBaseCenterY;
        layoutCache.sizeFingerprint = sizeFingerprint;
        layoutCache.ready = true;
      };

      /** Read circle box size before any refreshInit style writes (nav margin). */
      const cacheCircleBoxSize = () => {
        layoutCache.circleWidth = circle.offsetWidth;
        layoutCache.circleHeight = circle.offsetHeight;
      };

      const onRefreshInit = () => {
        cacheCircleBoxSize();
        syncStickyNavFlowGap();
      };

      const onScrollTriggerRefresh = () => {
        refreshLayoutCache();
        if (swirlClipsActive) {
          syncClipTransforms();
        }
      };

      // Size-only invalidation — next sync/refresh remeasures; no layout writes.
      const resizeObserver =
        typeof ResizeObserver !== 'undefined'
          ? new ResizeObserver(() => {
              layoutCache.ready = false;
              layoutCache.sizeFingerprint = '';
            })
          : null;
      resizeObserver?.observe(circle);

      const syncClipTransforms = () => {
        const t0 = heroSwirlPerf ? performance.now() : 0;
        let layoutReads = 0;
        let transformWrites = 0;

        if (!layoutCache.ready) {
          refreshLayoutCache();
          layoutReads += 1;
        }

        const y = Number(gsap.getProperty(circle, 'y'));
        const rotation = Number(gsap.getProperty(circle, 'rotation'));
        const scale = Number(gsap.getProperty(circle, 'scale'));
        const k = (layoutCache.circleWidth / HERO_SWIRL_CIRCLE_VIEWBOX.width) * scale;
        const circleCenterX = layoutCache.circleBaseCenterX;
        const circleCenterY = layoutCache.circleBaseCenterY + y;

        // Live border-box origins — sticky nav and pinned intro can sit at
        // different tops than a refresh-time cache; both must map the same
        // viewport circle into local `userSpaceOnUse` coords.
        const introRect = intro ? intro.getBoundingClientRect() : null;
        const navRect = stickyNav ? stickyNav.getBoundingClientRect() : null;
        if (introRect || navRect) {
          layoutReads += 1;
        }

        if (introRect) {
          writeClipTransform(
            clipPathShape,
            circleCenterX - introRect.left,
            circleCenterY - introRect.top,
            rotation,
            k,
          );
          transformWrites += 1;
        }

        if (navRect) {
          writeClipTransform(
            navClipPathShape,
            circleCenterX - navRect.left,
            circleCenterY - navRect.top,
            rotation,
            k,
          );
          transformWrites += 1;
        }

        if (heroSwirlPerf) {
          const durationMs = performance.now() - t0;
          void heroSwirlPerf.then((m) =>
            m.recordHeroSwirlSync({ durationMs, layoutReads, transformWrites }),
          );
        }
      };

      const clearClipPathTransforms = () => {
        clipPathShape.removeAttribute('transform');
        navClipPathShape.removeAttribute('transform');
      };

      /** Past travel: drop live clip styles, path transforms, and proxy will-change. */
      const tearDownSwirlClipScene = () => {
        releaseSwirlClips();
        clearClipPathTransforms();
        circle.style.setProperty(
          'will-change',
          HERO_SWIRL_PROXY_WILL_CHANGE_IDLE,
        );
      };

      /**
       * Re-enter travel (incl. scrub-back): pose paths before applying clips so
       * the identity swirl never flashes, then restore CSS will-change.
       */
      const armSwirlClipScene = () => {
        circle.style.removeProperty('will-change');
        syncClipTransforms();
        applySwirlClips();
      };

      // Track clip application so we only touch styles on threshold crossings
      // (progress < 1 ↔ ≥ 1), not every ScrollTrigger onUpdate. Do not apply
      // until paths are posed — identity clip flashes the full swirl.
      let swirlClipsActive = false;

      const syncSwirlClipForProgress = (progress: number) => {
        if (!intro && !stickyNav) return;
        if (progress >= 1) {
          if (swirlClipsActive) {
            tearDownSwirlClipScene();
            swirlClipsActive = false;
          }
        } else if (!swirlClipsActive) {
          armSwirlClipScene();
          swirlClipsActive = true;
        }
      };

      const syncClipTransformsWhileArmed = () => {
        if (!swirlClipsActive) return;
        syncClipTransforms();
      };

      const circleHeight = () =>
        layoutCache.circleHeight > 0
          ? layoutCache.circleHeight
          : circle.offsetHeight;
      const circleYFrom = () =>
        -(((1 - heroScene.circleStartScale) / 2) * circleHeight());
      const circleYTo = () =>
        -(
          (circleHeight() + window.innerHeight) / 2 +
          window.innerHeight * heroScene.circleCoverDrift
        );

      const circleScrollTriggerBase = {
        trigger: runway,
        start: 'top top',
        end: 'bottom bottom',
        scrub,
        invalidateOnRefresh: true,
        immediateRender: true,
      } as const;

      // Narrow: ease `y` in so the circle bottom stays below the fold until
      // scale covers; keep scale/rotation linear for even touch motion.
      // Desktop: single tween with one ease-in on all props.
      let circleTween: gsap.core.Tween;
      let circleScaleRotationTween: gsap.core.Tween | null = null;
      if (isNarrow) {
        circleTween = gsap.fromTo(
          circle,
          { y: circleYFrom },
          {
            y: circleYTo,
            ease: heroScene.narrow.circleTravelYEase,
            onUpdate: syncClipTransformsWhileArmed,
            scrollTrigger: {
              ...circleScrollTriggerBase,
              onUpdate: (self) => {
                syncSwirlClipForProgress(self.progress);
              },
            },
          },
        );
        // Scale/rotation shares the same scrub window — do not double-fire
        // syncClipTransforms (the y tween's onUpdate already reads latest props).
        circleScaleRotationTween = gsap.fromTo(
          circle,
          {
            rotation: heroScene.circleRotationFromDeg,
            scale: heroScene.circleStartScale,
          },
          {
            rotation: heroScene.circleRotationToDeg,
            scale: 1,
            ease: circleTravelEase,
            scrollTrigger: circleScrollTriggerBase,
          },
        );
      } else {
        circleTween = gsap.fromTo(
          circle,
          {
            y: circleYFrom,
            rotation: heroScene.circleRotationFromDeg,
            scale: heroScene.circleStartScale,
          },
          {
            y: circleYTo,
            rotation: heroScene.circleRotationToDeg,
            scale: 1,
            ease: circleTravelEase,
            onUpdate: syncClipTransformsWhileArmed,
            scrollTrigger: {
              ...circleScrollTriggerBase,
              onUpdate: (self) => {
                syncSwirlClipForProgress(self.progress);
              },
            },
          },
        );
      }

      // Pin off the runway (not intro's own top) so #introduction is fixed
      // from scroll 0. StickyNav between runway and intro can leave
      // `trigger: intro, start: 'top top'` slightly after scroll 0 — then the
      // clipped cream swirl document-scrolls 1:1 with the absolute Navbar.
      // StickyNav flow gap is cancelled above so pin top and unpin translateY
      // share the same geometry.
      if (intro) {
        ScrollTrigger.create({
          trigger: runway,
          start: 'top top',
          end: () =>
            `bottom+=${window.innerHeight * heroScene.introPinHoldViewportFraction} bottom`,
          pin: intro,
          invalidateOnRefresh: true,
        });
      }

      const catchUpScrub = (
        trigger: ScrollTrigger | undefined,
        animation: gsap.core.Animation,
      ) => {
        if (!trigger) return;
        animation.progress(trigger.progress);
      };

      // Wait for fonts before SplitText so line masks measure correctly.
      let contentCleanup: (() => void) | null = null;
      let cancelled = false;

      const attachContentReveal = () => {
        if (cancelled) return;
        const { timeline, cleanup } = createIntroContentReveal(runtime, {
          runway,
          scrub,
        });
        contentCleanup = cleanup;
        ScrollTrigger.refresh();
        if (timeline) {
          catchUpScrub(timeline.scrollTrigger, timeline);
        }
      };

      onRefreshInit();
      ScrollTrigger.addEventListener('refreshInit', onRefreshInit);
      ScrollTrigger.addEventListener('refresh', onScrollTriggerRefresh);
      ScrollTrigger.refresh();
      refreshLayoutCache();

      const circleTrigger = circleTween.scrollTrigger;
      catchUpScrub(circleTrigger, circleTween);
      if (circleScaleRotationTween) {
        catchUpScrub(
          circleScaleRotationTween.scrollTrigger,
          circleScaleRotationTween,
        );
      }
      // Arm or tear down from current scroll (pose before apply when arming).
      // If we start past travel (restore / deep link), mark done + idle GPU hints
      // even though clips were never applied — otherwise intro stays visibility:hidden.
      if (circleTrigger) {
        if (circleTrigger.progress >= 1) {
          tearDownSwirlClipScene();
          swirlClipsActive = false;
        } else {
          syncSwirlClipForProgress(circleTrigger.progress);
        }
      }

      markHeroSceneReady();

      if (document.fonts?.status === 'loaded') {
        attachContentReveal();
      } else if (document.fonts?.ready) {
        void document.fonts.ready.then(attachContentReveal);
      } else {
        attachContentReveal();
      }

      return () => {
        cancelled = true;
        resizeObserver?.disconnect();
        ScrollTrigger.removeEventListener('refreshInit', onRefreshInit);
        ScrollTrigger.removeEventListener('refresh', onScrollTriggerRefresh);
        contentCleanup?.();
        clearSwirlClipStyles();
        clearClipPathTransforms();
        clearStickyNavFlowGap();
        circle.style.removeProperty('will-change');
      };
    },
  );

  return () => {
    resetHeroSceneReady();
    matchMedia.revert();
    unsyncLenis();
  };
}
